import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc, 
  query, 
  orderBy,
  where,
  addDoc 
} from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { 
  FaUsers, 
  FaBan, 
  FaTrophy, 
  FaSearch, 
  FaFilter,
  FaUserShield,
  FaUserTimes,
  FaGift,
  FaEye,
  FaEdit
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [rewardForm, setRewardForm] = useState({
    badgeId: '',
    points: 0,
    message: ''
  });

  useEffect(() => {
    loadUsers();
    loadBadges();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, filterStatus]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // Charger tous les utilisateurs depuis les documents liés (fanzone_users, votes, etc.)
      const fanZoneQuery = query(collection(db, 'fanzone_users'), orderBy('dateCreation', 'desc'));
      const fanZoneSnapshot = await getDocs(fanZoneQuery);
      
      const usersMap = new Map();
      
      // Ajouter les utilisateurs de fanzone
      fanZoneSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        usersMap.set(userData.userId, {
          id: doc.id,
          userId: userData.userId,
          nom: userData.nom || userData.userNom,
          email: userData.email,
          statut: userData.statut || 'actif',
          role: userData.role || 'user',
          dateCreation: userData.dateCreation?.toDate(),
          derniereConnexion: userData.derniereConnexion?.toDate(),
          pointsTotal: userData.pointsTotal || 0,
          badgesCount: userData.badges?.length || 0,
          source: 'fanzone'
        });
      });

      // Charger également les données des posts du mur
      const postsQuery = query(collection(db, 'mur_supporters'));
      const postsSnapshot = await getDocs(postsQuery);
      
      postsSnapshot.docs.forEach(doc => {
        const postData = doc.data();
        const userId = postData.userId;
        
        if (!usersMap.has(userId)) {
          usersMap.set(userId, {
            id: userId,
            userId: userId,
            nom: postData.userNom,
            email: '',
            statut: 'actif',
            role: 'user',
            dateCreation: postData.dateCreation?.toDate(),
            derniereConnexion: postData.dateCreation?.toDate(),
            pointsTotal: 0,
            badgesCount: 0,
            source: 'mur'
          });
        }
      });

      // Charger les données des pronostics
      const pronosticsQuery = query(collection(db, 'pronostics'));
      const pronosticsSnapshot = await getDocs(pronosticsQuery);
      
      pronosticsSnapshot.docs.forEach(doc => {
        const pronosticData = doc.data();
        const userId = pronosticData.userId;
        
        if (!usersMap.has(userId)) {
          usersMap.set(userId, {
            id: userId,
            userId: userId,
            nom: pronosticData.userNom,
            email: '',
            statut: 'actif',
            role: 'user',
            dateCreation: pronosticData.dateCreation?.toDate(),
            derniereConnexion: pronosticData.dateCreation?.toDate(),
            pointsTotal: pronosticData.points || 0,
            badgesCount: 0,
            source: 'pronostics'
          });
        } else {
          // Ajouter les points des pronostics
          const existingUser = usersMap.get(userId);
          existingUser.pointsTotal += pronosticData.points || 0;
        }
      });

      const usersArray = Array.from(usersMap.values());
      setUsers(usersArray);
    } catch (error) {
      logger.error('Erreur loadUsers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBadges = async () => {
    try {
      const badgesQuery = query(collection(db, 'badges'));
      const badgesSnapshot = await getDocs(badgesQuery);
      const badges = badgesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableBadges(badges);
    } catch (error) {
      logger.error('Erreur loadBadges:', error);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrer par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => user.statut === filterStatus);
    }

    setFilteredUsers(filtered);
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      const userDocRef = doc(db, 'fanzone_users', userId);
      await updateDoc(userDocRef, {
        statut: newStatus,
        dateModification: new Date()
      });

      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, statut: newStatus } : user
      ));

      toast.success(`Utilisateur ${newStatus === 'banni' ? 'banni' : 'débanni'} avec succès`, {
        icon: newStatus === 'banni' ? '🚫' : '✅',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } catch (error) {
      logger.error('Erreur updateUserStatus:', error);
      toast.error('Erreur lors de la mise à jour du statut', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const promoteUser = async (userId) => {
    try {
      const userDocRef = doc(db, 'fanzone_users', userId);
      await updateDoc(userDocRef, {
        role: 'admin',
        dateModification: new Date()
      });

      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: 'admin' } : user
      ));

      toast.success('Utilisateur promu administrateur avec succès', {
        icon: '👑',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } catch (error) {
      logger.error('Erreur promoteUser:', error);
      toast.error('Erreur lors de la promotion', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const rewardUser = async () => {
    if (!selectedUser || !rewardForm.badgeId) return;

    try {
      // Ajouter le badge à l'utilisateur
      const userDocRef = doc(db, 'fanzone_users', selectedUser.id);
      const badge = availableBadges.find(b => b.id === rewardForm.badgeId);
      
      await updateDoc(userDocRef, {
        [`badges.${rewardForm.badgeId}`]: {
          id: rewardForm.badgeId,
          nom: badge.nom,
          dateObtention: new Date(),
          source: 'admin_reward'
        },
        pointsTotal: selectedUser.pointsTotal + parseInt(rewardForm.points || 0),
        dateModification: new Date()
      });

      // Ajouter une notification
      if (rewardForm.message) {
        await addDoc(collection(db, 'notifications'), {
          userId: selectedUser.userId,
          type: 'reward',
          titre: 'Récompense reçue !',
          message: rewardForm.message,
          badgeId: rewardForm.badgeId,
          points: parseInt(rewardForm.points || 0),
          dateCreation: new Date(),
          lu: false
        });
      }

      toast.success('Récompense accordée avec succès !', {
        icon: '🎁',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      setShowRewardModal(false);
      setRewardForm({ badgeId: '', points: 0, message: '' });
      loadUsers();
    } catch (error) {
      logger.error('Erreur rewardUser:', error);
      toast.error('Erreur lors de l\'attribution de la récompense', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'actif': return 'bg-green-100 text-green-800';
      case 'banni': return 'bg-red-100 text-red-800';
      case 'suspendu': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'moderator': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <FaUsers className="text-3xl text-blue-600" />
          <h1 className="text-3xl font-bold">Gestion des Utilisateurs</h1>
        </div>
        <p className="text-gray-600">
          Gérez les utilisateurs, leurs permissions et récompensez les membres actifs
        </p>
      </motion.div>

      {/* Filtres et recherche */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 mb-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FaFilter className="text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous</option>
                <option value="actif">Actifs</option>
                <option value="banni">Bannis</option>
                <option value="suspendu">Suspendus</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Liste des utilisateurs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden"
      >
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des utilisateurs...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Badges
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière activité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user, index) => (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.nom}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.statut)}`}>
                        {user.statut}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.pointsTotal}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.badgesCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.derniereConnexion?.toLocaleDateString('fr-FR') || 'Jamais'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowRewardModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 transition-colors"
                          title="Récompenser"
                        >
                          <FaGift />
                        </button>
                        
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => promoteUser(user.id)}
                            className="text-purple-600 hover:text-purple-900 transition-colors"
                            title="Promouvoir admin"
                          >
                            <FaUserShield />
                          </button>
                        )}
                        
                        {user.statut === 'actif' ? (
                          <button
                            onClick={() => updateUserStatus(user.id, 'banni')}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Bannir"
                          >
                            <FaBan />
                          </button>
                        ) : (
                          <button
                            onClick={() => updateUserStatus(user.id, 'actif')}
                            className="text-green-600 hover:text-green-900 transition-colors"
                            title="Débannir"
                          >
                            <FaUserTimes />
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Modal de récompense */}
      <AnimatePresence>
        {showRewardModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <FaTrophy className="text-2xl text-yellow-500" />
                <h2 className="text-xl font-bold">Récompenser {selectedUser.nom}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Badge à attribuer
                  </label>
                  <select
                    value={rewardForm.badgeId}
                    onChange={(e) => setRewardForm(prev => ({ ...prev, badgeId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner un badge</option>
                    {availableBadges.map(badge => (
                      <option key={badge.id} value={badge.id}>
                        {badge.nom} - {badge.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Points bonus
                  </label>
                  <input
                    type="number"
                    value={rewardForm.points}
                    onChange={(e) => setRewardForm(prev => ({ ...prev, points: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message personnel
                  </label>
                  <textarea
                    value={rewardForm.message}
                    onChange={(e) => setRewardForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Message de félicitations..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => setShowRewardModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={rewardUser}
                  disabled={!rewardForm.badgeId}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Récompenser
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsers;